import { apiService } from './api'

// 対象者タイプ
export type TargetType = 'group' | 'department' | 'position' | 'individual'

// 対象者指定
export interface AssignmentTarget {
  type: TargetType
  ids: string[]
}

// スケジュール設定
export interface ScheduleSettings {
  startDate: string
  endDate: string
  dueDate: string
}

// 対象者プレビューレスポンス
export interface TargetPreviewResponse {
  totalUsers: number
  duplicateCount: number
  breakdown: {
    group: { count: number; users: UserPreview[] }
    department: { count: number; users: UserPreview[] }
    position: { count: number; users: UserPreview[] }
    individual: { count: number; users: UserPreview[] }
  }
}

export interface UserPreview {
  id: string
  name: string
  email: string
  department?: string
  role: string
}

// スケジュール割り当て結果
export interface AssignmentResult {
  groupId: string
  groupName: string
  groupEnrollmentId: string
  status: 'assigned' | 'no_members'
  assignedCount: number
  totalMembers?: number
  targetType?: string
  targetName?: string
  message?: string
}

export interface AssignmentResponse {
  message: string
  test: {
    id: string
    title: string
    category: string
  }
  schedule: {
    startDate: string
    endDate: string
    dueDate: string
  }
  totalAssigned: number
  results: AssignmentResult[]
}

// グループスケジュール
export interface GroupSchedule {
  id: string
  userGroupId: string
  testId: string
  dueDate?: string
  startDate?: string
  endDate?: string
  autoStart: boolean
  autoEnd: boolean
  timezone: string
  isActive: boolean
  assignedAt: string
  userGroup: {
    id: string
    name: string
    description?: string
    _count?: { members: number }
  }
  test: {
    id: string
    title: string
    description?: string
    passingScore: number
    timeLimit?: number
    complianceRule: {
      title: string
      category: string
    }
  }
  progressInfo: {
    totalCount: number
    completedCount: number
    progressRate: number
    scheduleStatus: string
    statusBreakdown: {
      ASSIGNED: number
      IN_PROGRESS: number
      COMPLETED: number
      OVERDUE: number
    }
    remainingCount: number
    isCompleted: boolean
  }
  status: 'active' | 'upcoming' | 'ended' | 'inactive' | 'no_schedule'
}

// スケジュール詳細
export interface ScheduleDetail extends GroupSchedule {
  enrollments: EnrollmentDetail[]
}

export interface EnrollmentDetail {
  id: string
  user: {
    id: string
    name: string
    email: string
    department?: string
  }
  status: 'ASSIGNED' | 'IN_PROGRESS' | 'COMPLETED' | 'OVERDUE'
  assignedAt: string
  startedAt?: string
  lastAccessAt?: string
  latestResult?: {
    score: number
    passed: boolean
    completedAt: string
  }
}

// スケジュール一覧レスポンス
export interface GroupSchedulesResponse {
  groupSchedules: GroupSchedule[]
}

// テスト一覧レスポンス
export interface TestOption {
  id: string
  title: string
  description?: string
  passingScore: number
  timeLimit?: number
  complianceRule: {
    title: string
    category: string
  }
  questionCount: number
}

export interface TestsResponse {
  tests: TestOption[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

// 部署一覧レスポンス
export interface DepartmentsResponse {
  departments: string[]
}

// 役職一覧レスポンス
export interface PositionsResponse {
  positions: string[]
}

class ScheduleService {
  /**
   * グループスケジュール一覧取得
   */
  async getGroupSchedules(): Promise<GroupSchedulesResponse> {
    return apiService.get<GroupSchedulesResponse>('/enrollments/group-schedules')
  }

  /**
   * グループスケジュール詳細取得
   */
  async getGroupScheduleDetail(id: string): Promise<ScheduleDetail> {
    return apiService.get<ScheduleDetail>(`/enrollments/group-schedules/${id}`)
  }

  /**
   * グループスケジュール更新
   */
  async updateGroupSchedule(
    id: string,
    data: {
      startDate?: string
      endDate?: string
      dueDate?: string
      isActive?: boolean
    }
  ): Promise<{ message: string; schedule: GroupSchedule }> {
    return apiService.put(`/enrollments/group-schedules/${id}`, data)
  }

  /**
   * グループスケジュール削除
   */
  async deleteGroupSchedule(id: string): Promise<{ message: string }> {
    return apiService.delete(`/enrollments/group-schedules/${id}`)
  }

  /**
   * 対象者プレビュー
   */
  async previewTargets(targets: AssignmentTarget[]): Promise<TargetPreviewResponse> {
    return apiService.post<TargetPreviewResponse>('/enrollments/preview-targets', { targets })
  }

  /**
   * 複合対象者への割り当て
   */
  async assignTestToTargets(
    testId: string,
    targets: AssignmentTarget[],
    schedule: ScheduleSettings
  ): Promise<AssignmentResponse> {
    return apiService.post<AssignmentResponse>('/enrollments/assign-targets', {
      testId,
      targets,
      schedule,
    })
  }

  /**
   * テスト一覧取得
   */
  async getTests(params?: {
    page?: number
    limit?: number
    search?: string
    category?: string
  }): Promise<TestsResponse> {
    return apiService.get<TestsResponse>('/tests', params)
  }

  /**
   * 部署一覧取得
   */
  async getDepartments(): Promise<DepartmentsResponse> {
    return apiService.get<DepartmentsResponse>('/users/departments')
  }

  /**
   * 役職一覧取得
   */
  async getPositions(): Promise<PositionsResponse> {
    return apiService.get<PositionsResponse>('/users/positions')
  }

  /**
   * ユーザー一覧取得（個人選択用）
   */
  async getUsers(params?: {
    page?: number
    limit?: number
    search?: string
    department?: string
    role?: string
  }): Promise<{
    users: UserPreview[]
    pagination: { page: number; limit: number; total: number; pages: number }
  }> {
    return apiService.get('/users', params)
  }

  /**
   * グループスケジュール即時リマインド送信
   */
  async sendGroupScheduleReminder(id: string): Promise<ReminderResponse> {
    return apiService.post<ReminderResponse>(`/enrollments/group-schedules/${id}/remind`, {})
  }

  /**
   * グループスケジュールレポート取得
   */
  async getGroupScheduleReport(id: string): Promise<ScheduleReport> {
    return apiService.get<ScheduleReport>(`/enrollments/group-schedules/${id}/report`)
  }
}

// レポート用の受講詳細
export interface ReportEnrollmentDetail {
  id: string
  user: {
    id: string
    name: string
    email: string
    department?: string
  }
  status: 'ASSIGNED' | 'IN_PROGRESS' | 'COMPLETED' | 'OVERDUE'
  assignedAt: string
  startedAt?: string
  lastAccessAt?: string
  latestResult?: {
    score: number
    passed: boolean
    completedAt: string
  }
  isOnTime: boolean
}

// スケジュールレポート
export interface ScheduleReport {
  scheduleInfo: {
    id: string
    testId: string
    testTitle: string
    testDescription?: string
    category?: string
    passingScore: number
    groupId: string
    groupName: string
    groupDescription?: string
    startDate?: string
    endDate?: string
    dueDate?: string
    isActive: boolean
    assignedAt: string
  }
  report: {
    completionRate: number // 受講完了率 %
    onTimeCompletionRate: number // 期限内完了率 %
    averageScore: number // 平均スコア
    notStartedCount: number // 未受講者数
    totalCount: number // 全対象者数
    completedCount: number // 完了者数
    onTimeCompletedCount: number // 期限内完了者数
    passRate: number // 合格率
    passedCount: number // 合格者数
    inProgressCount: number // 進行中
    overdueCount: number // 期限切れ
  }
  enrollments: ReportEnrollmentDetail[]
}

// リマインド送信結果
export interface ReminderResponse {
  message: string
  schedule: {
    id: string
    groupName: string
    testTitle: string
    dueDate: string | null
    daysLeft: number | null
  }
  sentCount: number
  failedCount: number
  totalTargeted: number
  details: {
    userId: string
    userName: string
    userEmail: string
    status: string
    success: boolean
    error?: string
  }[]
}

export const scheduleService = new ScheduleService()
